import type { Ref } from 'react';
import type { TRPCOptionsProxy, inferOutput } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@servwell/api/router';
import { Button } from '../ui/Button';

/** A draft's warnings, counted by the server (`invoice.get`'s `issueWarnings`); null once issued. */
export type IssueWarnings = NonNullable<
  inferOutput<TRPCOptionsProxy<AppRouter>['invoice']['get']>
>['issueWarnings'];

/**
 * What Issue warns about, one sentence per count that isn't zero (Inventory v2 Phase 1, ticket
 * 07, Q5). The lines are the ones the badges flag, so the two can't disagree.
 */
export function issueWarningLines(warnings: IssueWarnings): string[] {
  if (!warnings) return [];
  const { noPrice, billedNotUsed } = warnings;
  return [
    ...(noPrice > 0
      ? [noPrice === 1 ? '1 line has no price' : `${noPrice} lines have no price`]
      : []),
    ...(billedNotUsed > 0
      ? [
          billedNotUsed === 1
            ? '1 part line billed, not used'
            : `${billedNotUsed} part lines billed, not used`,
        ]
      : []),
  ];
}

/**
 * The draft's Issue button. With nothing to warn about it issues; with a warning it asks first
 * (`onAsk`), and the page shows `IssueAnyway`. It never blocks: the server issues either way.
 * Hook-free, so a test can press it as the page renders it.
 */
export function IssueButton({
  warnings,
  pending,
  onIssue,
  onAsk,
  buttonRef,
}: {
  warnings: IssueWarnings;
  pending: boolean;
  onIssue: () => void;
  onAsk: () => void;
  /** Where focus returns when the ask is closed, so it never drops to the page. */
  buttonRef?: Ref<HTMLButtonElement>;
}) {
  const asksFirst = issueWarningLines(warnings).length > 0;
  return (
    <Button ref={buttonRef} onClick={asksFirst ? onAsk : onIssue} disabled={pending}>
      {pending ? 'Issuing…' : 'Issue invoice'}
    </Button>
  );
}

/**
 * The ask: the counts, then "Issue anyway?". A warning, not a danger: issuing a draft with a
 * line to check is allowed, so the way on is the primary button and the way back is beside it.
 */
export function IssueAnyway({
  warnings,
  pending,
  onIssue,
  onKeep,
}: {
  warnings: IssueWarnings;
  pending: boolean;
  onIssue: () => void;
  onKeep: () => void;
}) {
  const lines = issueWarningLines(warnings);
  // Fixed meanwhile (a refetch came back clean): nothing to ask, and Issue issues next press.
  if (lines.length === 0) return null;
  return (
    <div
      className="notice badge--warn space-y-2"
      role="group"
      aria-label="Issue anyway?"
      // Escape is the way back, as "Check the lines" is.
      onKeyDown={(e) => {
        if (e.key === 'Escape') onKeep();
      }}
    >
      <p className="text-sm font-medium">Issue anyway?</p>
      <ul className="list-disc pl-5 text-sm">
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
      <p className="text-xs">
        The flagged lines are marked below. Issuing gives the invoice its number and locks
        the amounts as they are.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={onIssue} disabled={pending}>
          {pending ? 'Issuing…' : 'Issue anyway'}
        </Button>
        {/* Focus lands on the way back, so a stray Enter never issues. */}
        <Button variant="ghost" onClick={onKeep} autoFocus>
          Check the lines
        </Button>
      </div>
    </div>
  );
}
